import React, { forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { DisplaySpinnerContext, RouteGuardContext, TabPanelContext } from "../../../../context/ContextIndex";
import { selectSessionConfig } from "../../../../store/store-selector";
import { AppForm, DynamicFormContainer } from "../../../../components";
import { getConsumableProductDefaultValue } from "./schema";
import { getReplacementProductDefaultValue } from "../../replacement-product/create/schema";
import _ from "lodash";
import { getReplacementProduct } from "../../api-handler/api-handler";

const ConsumableProductCreate = forwardRef(({ setConsumableProductFormData, setIsFormValid }, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const { formState, clearErrors, watch } = useFormContext();
  const _tabContextObject = useContext(TabPanelContext);
  const _tabContext = _tabContextObject.getTabFormContext();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const routeGuardContext = useContext(RouteGuardContext);

  const EditConsumableProductId = searchParams.get("consumableProductid");
  const navigationData = location.state;
  const settingsConfig = useSelector(selectSessionConfig);

  useImperativeHandle(ref, () => ({
    tabChangeHandler,
  }));

  //Event Handlers
  const handleCancel = () => {
    try {
      navigate("/consumableproductlist");
    } catch (err) {
      alert(err);
    }
  };

  const tabChangeHandler = (newtab) => {
    if (formState.isValid) {
      onConsumableProductSubmit(newtab);
    }
    console.log(formState);
  };

  const onConsumableProductSubmit = (newtab = "1") => {
    try {
      const _formStateData = watch();
      clearErrors();
      _tabContextObject.setTabFormContext({
        tabIndex: newtab,
        data: {
          ..._tabContext.data,
          consumableProduct: _formStateData,
        },
        isDirty: EditConsumableProductId ? !_.isEqual(_tabContext.prevData?.consumableProduct, _formStateData) : true,
      });
    } catch (e) {
      alert(e);
    }
  };

  //Use Effects
  useEffect(() => {
    const setFormSchema = () => {
      try {
        let _formData = navigationData ? { ...navigationData } : null;
        if (_formData && EditConsumableProductId) {
          _tabContextObject.setTabFormContext({
            editConsumableProductId: EditConsumableProductId,
            prevData: {
              consumableProduct: getConsumableProductDefaultValue(_formData),
            },
            isDirty: false,
          });

          setConsumableProductFormData((prevstate) => ({
            ...prevstate,
            appformdata: getConsumableProductDefaultValue(
              _tabContext.data?.consumableProduct ? _tabContext.data?.consumableProduct : _formData
            ),
          }));
        }
      } catch (err) {
        alert(err);
      }
    };

    const fetchReplacementProducts = async () => {
      try {
        if (EditConsumableProductId && !_tabContext?.data?.replacementProducts) {
          // Make the API call here to get the replacement products

          let response = await getReplacementProduct(EditConsumableProductId, settingsConfig);

          console.log("_tabContext.prevData");
          console.log(_tabContext.prevData);

          // Store the fetched data in the context
          _tabContextObject.setTabFormContext({
            data: {
              replacementProducts: response.map((item, index) => ({
                id: index,
                ...getReplacementProductDefaultValue(item)
              })),
            },
            prevData: {
              replacementProducts: response,
            },
          });
        }
      } catch (err) {
        alert("Failed to fetch replacement products.");
      } finally {
        spinnerCtx.setLoadingIndicator(false);
      }
    };

    spinnerCtx.setLoadingIndicator(true);
    setFormSchema();
    fetchReplacementProducts();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [EditConsumableProductId, navigationData]);

  useEffect(() => {
    routeGuardContext.setFormState(_tabContext?.isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppForm onSubmit={onConsumableProductSubmit} setFormValid={setIsFormValid}>
      <DynamicFormContainer
        header={t("createConsumableProduct.createConsumableProduct")}
        onCancel={handleCancel}
        onSaveLabel={t("generic.next")}
        isWithAppForm={false}
        onSubmit={onConsumableProductSubmit}
      />
    </AppForm>
  );
});

export default ConsumableProductCreate;
